import React, { forwardRef, useImperativeHandle } from "react";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
const MultiSelectComponent = forwardRef((props, ref) => {
  const {
    style,
    placeholder,
    loading = false,
    dataList,
    name,
    module,
    textField,
    dataItemKey,
    valueData,
    data,
    appendTo,
    autoClose,
    autoBind,
    isSelectALl,
    onOpen,
    onChange,
    onFilterChange,
    disabled,
    className,
    selectAllId = "Select All",
    onClose,
    selectionLimit = null,
    isPeriodDropdown = false,
    source,
    preserveFilteredDataOnSelect = false,
    passFilterEvent = false,
    allowEmptyWhenNoOptions = false,
  } = props;
  const innerRef = React.useRef(null);
  const isSelectingRef = React.useRef(false);

  const safeData = Array.isArray(data) ? data : [];
  const safeDataList = Array.isArray(dataList) ? dataList : [];

  const [search, setSearch] = React.useState("");
  const [filterValue, SetFilterValue] = React.useState("");

  const isManualFilterClear = (event) => {
    const filterText = event?.filter?.value;
    const eventType = event?.syntheticEvent?.type;
    const inputType = event?.syntheticEvent?.nativeEvent?.inputType;

    return (
      filterText === "" &&
      (eventType === "input" ||
        inputType === "deleteContentBackward" ||
        inputType === "deleteContentForward" ||
        inputType === "deleteByCut")
    );
  };

  useImperativeHandle(ref, () => ({
    focusInput() {
      if (!innerRef.current) return;

      // ✅ Try to focus the real HTML input inside Kendo's wrapper
      const inputEl = innerRef.current.element?.querySelector("input");
      if (inputEl) {
        inputEl.focus(); // ✅ puts cursor inside
      } else {
        // fallback
        innerRef.current.focus?.();
      }
    },
  }));

  const filterDropdownChangeValue = (event) => {
    if (
      preserveFilteredDataOnSelect &&
      isSelectingRef.current &&
      !event?.filter?.value &&
      !isManualFilterClear(event)
    ) {
      return;
    }
    if (isPeriodDropdown) {
      if (valueData && valueData.length >= selectionLimit) {
        return;
      }
    }
    let searchValue = event;
    handleFocus();
    if (source === "query-builder") {
      const inputEl = innerRef.current?.element?.querySelector(".k-input");
      const value = inputEl?.value;
      searchValue.filter.value = value;
    }
    setSearch(searchValue.filter);
    SetFilterValue(searchValue.filter.value);
    let dataValue = filterBy(dataList.slice(), searchValue.filter);
    if (passFilterEvent) {
      onFilterChange?.(dataValue, searchValue);
      return;
    }
    onFilterChange?.(dataValue);
  };

  const onOpenValue = () => {
    let dataValue = filterValue && filterValue !== "" ? filterBy(safeData.slice(), search) : safeDataList;
    onOpen && onOpen(dataValue);
  };

  const handleChangeDropdownValue = (e) => {
    if (preserveFilteredDataOnSelect) {
      isSelectingRef.current = true;
    }
    if (isPeriodDropdown) {
      if (selectionLimit && e.value.length > selectionLimit) {
        isSelectingRef.current = false;
        return;
      }
    }
    const name = e.name ? e.name : e.target.name;
    if (isSelectALl) {
      if (valueData && valueData.filter((x) => x.text === "Select All").length > 0) {
        let index = e.value.findIndex((x) => x.text === "Select All");
        if (index >= 0) e.value.splice(index, 1);
      } else if (e.value && e.value.filter((x) => x.text === "Select All").length > 0 && e.value.length > 1) {
        e.value = [];
        e.value.push({ text: "Select All", id: selectAllId });
      }
      const hasNoOptions = safeData.length === 0 && safeDataList.length === 0 && !loading;
      if (e.value.length === 0 && !(allowEmptyWhenNoOptions && hasNoOptions)) {
        e.value.push({ text: "Select All", id: selectAllId });
      }
      onChange(e.value, name, e);
    } else {
      onChange(e.value, name, e);
    }
    SetFilterValue(filterValue);
    if (preserveFilteredDataOnSelect) {
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 300);
    }
  };

  const listLoadingRender = (element) => {
    const noData = (
      <h4
        style={{
          fontSize: "1em",
        }}
      >
        <br />
        Loading
      </h4>
    );
    return React.cloneElement(element, { ...element.props }, noData);
  };

  const listNoDataRender = (element) => {
    const noData = (
      <h4
        style={{
          fontSize: "1em",
        }}
      >
        <br />
        No data found
      </h4>
    );
    return React.cloneElement(element, { ...element.props }, noData);
  };

  const handleBlur = (e) => {
    SetFilterValue("");
  };

  const handleFocus = () => {};

  const handleOnClose = () => {
    onClose?.();
  };

  return (
    <MultiSelect
      name={name}
      data={safeData}
      filter={filterValue}
      textField={textField}
      onOpen={() => {
        onOpenValue(module);
      }}
      dataItemKey={dataItemKey}
      value={valueData}
      onChange={(e) => {
        handleChangeDropdownValue(e);
      }}
      filterable={true}
      onFilterChange={(e) => {
        filterDropdownChangeValue(e, module);
      }}
      onClose={handleOnClose}
      popupSettings={{ appendTo: appendTo }}
      autoClose={autoClose || false}
      autoBind={autoBind}
      loading={loading}
      placeholder={placeholder}
      style={style}
      onBlur={handleBlur}
      onFocus={handleFocus}
      listNoDataRender={
        loading
          ? listLoadingRender
          : loading === false && safeData && safeData.length === 0
            ? listNoDataRender
            : listNoDataRender
      }
      disabled={disabled}
      ref={innerRef}
      className={className}
    />
  );
});

export default React.memo(MultiSelectComponent);
